import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Instagram, Facebook, Youtube, Twitter, Linkedin, ExternalLink, Share2 } from "lucide-react";

// Pretty metadata per platform
const PLATFORMS = {
  tiktok: {
    label: "TikTok",
    gradient: "from-[#010101] via-[#25F4EE] to-[#FE2C55]",
    cardBg: "bg-black",
    text: "text-white",
    icon: (
      // Minimal inline TikTok glyph (lucide doesn't ship one)
      <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1Z"/>
      </svg>
    ),
    cta: "Regarder nos TikToks",
  },
  instagram: {
    label: "Instagram",
    gradient: "from-[#833AB4] via-[#FD1D1D] to-[#FCB045]",
    cardBg: "bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCB045]",
    text: "text-white",
    icon: <Instagram size={26} />,
    cta: "Voir notre feed Instagram",
  },
  facebook: {
    label: "Facebook",
    gradient: "from-[#1877F2] to-[#0C4FA3]",
    cardBg: "bg-gradient-to-br from-[#1877F2] to-[#0C4FA3]",
    text: "text-white",
    icon: <Facebook size={26} />,
    cta: "Suivre notre page Facebook",
  },
  youtube: {
    label: "YouTube",
    gradient: "from-[#FF0000] to-[#B00000]",
    cardBg: "bg-gradient-to-br from-[#FF0000] to-[#B00000]",
    text: "text-white",
    icon: <Youtube size={26} />,
    cta: "S'abonner à la chaîne",
  },
  twitter: {
    label: "X / Twitter",
    gradient: "from-[#000000] to-[#333333]",
    cardBg: "bg-black",
    text: "text-white",
    icon: <Twitter size={26} />,
    cta: "Nous suivre sur X",
  },
  linkedin: {
    label: "LinkedIn",
    gradient: "from-[#0A66C2] to-[#004182]",
    cardBg: "bg-gradient-to-br from-[#0A66C2] to-[#004182]",
    text: "text-white",
    icon: <Linkedin size={26} />,
    cta: "Nous suivre sur LinkedIn",
  },
  other: {
    label: "Autre",
    gradient: "from-slate-700 to-slate-900",
    cardBg: "bg-slate-800",
    text: "text-white",
    icon: <Share2 size={26} />,
    cta: "Ouvrir le lien",
  },
};

export default function Social() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/socials")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="social-page">
      {/* HERO */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold mb-3">Suivez-nous</div>
          <h1 className="font-bold leading-tight text-4xl sm:text-5xl lg:text-6xl" style={{ fontFamily: "Outfit" }}>
            Nos réseaux <span className="text-[#D97706]">sociaux</span>.
          </h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">
            Suivez le quotidien du club, les coulisses des matchs, les buts de la semaine et les ambiances de vestiaire.
            Un seul clic pour nous retrouver sur vos plateformes préférées — et pour partager autour de vous !
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {loading ? (
          <div className="text-center text-sm text-[#475569]">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="p-8 border border-dashed border-[#E2E8F0] rounded-lg text-center text-sm text-[#475569]" data-testid="social-empty">
            Aucun compte réseau social n'a encore été ajouté.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="social-grid">
            {items.map((s) => {
              const meta = PLATFORMS[s.platform] || PLATFORMS.other;
              return (
                <a
                  key={s.id}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${s.platform}-${s.id}`}
                  className={`${meta.cardBg} ${meta.text} rounded-xl p-6 hover:-translate-y-1 transition-all duration-200 shadow-lg hover:shadow-2xl flex flex-col`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      {meta.icon}
                    </div>
                    <ExternalLink size={16} className="opacity-60" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] tracking-widest uppercase font-bold opacity-80">{meta.label}</div>
                    <div className="mt-1 text-lg font-bold break-all leading-snug" style={{ fontFamily: "Outfit" }}>{s.handle}</div>
                  </div>
                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold">
                    {meta.cta} →
                  </div>
                </a>
              );
            })}
          </div>
        )}

        {/* Help text */}
        <div className="mt-10 p-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg flex items-start gap-3" data-testid="social-help">
          <Share2 size={18} className="text-[#1E3A8A] shrink-0 mt-0.5" />
          <div className="text-sm text-[#475569] leading-relaxed">
            <strong className="text-[#0F172A]">Aidez-nous à faire grandir le club :</strong> partagez nos publications autour de vous,
            taguez vos amis sous nos vidéos et abonnez-vous. Chaque abonné compte pour la visibilité du Villeurbanne United FC !
          </div>
        </div>
      </section>
    </div>
  );
}
